import React, {
  useCallback, useContext, useRef, useState,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DropResult } from 'react-beautiful-dnd';
import SortIcon from '@mui/icons-material/Sort';
import CaseGenerator from './CaseGenerator';
import { DigitalCase } from '../../../redux/types';
import CaseEditorList from './CaseEditorList';
import SortableList from '../../layout/SortableList';
import { getLastSortIndex } from '../../../utilities/arrayHelper';
import SurveyOptionContext from '../../../views/admin/SurveyOptionContext';

type Props = {
  cases: DigitalCase[];
  sectionId: number;
  surveyId: number;
  onGenerateCases: (generatedCases: DigitalCase[]) => void;
  onAddCase: (newCase: Omit<DigitalCase, 'id'>) => void;
  onUpdateCase: (updatedCase: DigitalCase) => void;
  onDeleteCase: (caseId: number) => void;
  onSortSave: (caseList: DigitalCase[]) => Promise<void>
};

const CaseEditor: React.FC<Props> = ({
  cases,
  sectionId,
  surveyId,
  onGenerateCases,
  onAddCase,
  onUpdateCase,
  onDeleteCase,
  onSortSave,
}: Props) => {
  const sortedListRef = useRef<DigitalCase[]>();
  const [sortMode, setSortMode] = useState<boolean>(false);
  const { disableEdit } = useContext(SurveyOptionContext);

  const onDragHandler = useCallback((result: DropResult, sortedList?: DigitalCase[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  const addCaseHandler = (caseName: string) => {
    const sortIdx = getLastSortIndex<DigitalCase>(cases);
    const newCase: Omit<DigitalCase, 'id'> = {
      digitalSectionId: sectionId,
      digitalSurveyId: surveyId,
      name: caseName,
      sortIndex: sortIdx,
    };
    onAddCase(newCase);
  };

  const updateCaseHandler = (updatedCase: DigitalCase) => {
    onUpdateCase(updatedCase);
  };

  const cancelSortHandler = () => {
    setSortMode(false);
    sortedListRef.current = undefined;
  };

  const saveSortOrderHandler = () => {
    const sortedList = sortedListRef.current;
    if (!sortedList) {
      return;
    }
    const updatedList = sortedList.map((caseObj, index) => {
      const updatedSortIndex: DigitalCase = { ...caseObj, sortIndex: index };
      return updatedSortIndex;
    });
    onSortSave(updatedList)
      .finally(() => {
        setSortMode(false);
        sortedListRef.current = undefined;
      });
  };

  return (cases.length ? (
    <Box>
      {
        !sortMode ? (
          <Box display="flex">
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Add, Update or Delete Cases</Typography>
            <Tooltip title="Sort" placement="top">
              <>
                <IconButton
                  disabled={disableEdit}
                  onClick={() => setSortMode(true)}
                >
                  <SortIcon />
                </IconButton>
              </>
            </Tooltip>
          </Box>
        ) : (
          <Box display="flex" gap={1}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Reorder Cases</Typography>
            <Button size="small" variant="outlined" onClick={saveSortOrderHandler}>Save</Button>
            <Button size="small" variant="outlined" color="secondary" onClick={cancelSortHandler}>Cancel</Button>
          </Box>
        )
      }
      {
        !sortMode ? (
          <CaseEditorList
            caseList={cases}
            onAddCase={addCaseHandler}
            onUpdateCase={updateCaseHandler}
            onDeleteCase={onDeleteCase}
          />
        ) : (
          <SortableList<DigitalCase>
            list={cases}
            droppableId="casesOrganizer"
            onDragEndHandler={onDragHandler}
          />
        )
      }
    </Box>
  ) : (
    <CaseGenerator
      onGenerateCases={onGenerateCases}
      sectionId={sectionId}
      surveyId={surveyId}
    />
  ));
};

export default CaseEditor;
